// useConversations — hook för att hantera chat-historik (DB-backed).
// Ersätter den tidigare ephemeral-in-memory-state i AiAssistantDialog.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { callRpc } from '@/lib/supabaseTyped';
import { useAuth } from '@/contexts/AuthContext';
import type { Intent, IntentSource } from '@/lib/intentTypes';

export type ConversationScope = 'workflow_ai' | 'admin_ai' | 'atlas_ai' | 'final_ai';

export interface ConversationRow {
  id: string;
  title: string;
  scope: ConversationScope;
  archived_at: string | null;
  last_activity_at: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
  message_count: number;
}

export interface ChatMessageRow {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  intent: Intent | null;
  intent_override: Intent | null;
  intent_confidence: number | null;
  intent_source: IntentSource | null;
  moderation_event_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export function useConversations(scope: ConversationScope, includeArchived = false) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['conversations', scope, includeArchived],
    queryFn: async (): Promise<ConversationRow[]> => {
      const { data } = await callRpc<ConversationRow[]>('list_my_conversations', {
        p_scope: scope,
        p_include_archived: includeArchived,
        p_limit: 100,
      });
      return (data ?? []) as ConversationRow[];
    },
    enabled: isAuthenticated,
    staleTime: 15_000,
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ['conversation-messages', conversationId],
    queryFn: async (): Promise<ChatMessageRow[]> => {
      if (!conversationId) return [];
      const { data, error } = await callRpc<ChatMessageRow[]>('list_conversation_messages', {
        p_conversation_id: conversationId,
        p_limit: 500,
      });
      if (error) {
        // C9: kasta istället för silent fallback så UI kan surface:a toast.
        // Vanligaste orsaker: migration 20260424100000 inte applicerad,
        // signatur-drift, eller 400 Bad Request från PostgREST schema-cache.
        console.warn('[list_conversation_messages] RPC failed', {
          conversationId, message: error.message,
        });
        throw new Error(error.message || 'list_conversation_messages failed');
      }
      return (data ?? []) as ChatMessageRow[];
    },
    enabled: !!conversationId,
    staleTime: 5_000,
    retry: false, // 400 är inte transient — retry löser inget
  });
}

export function useConversationMutations() {
  const queryClient = useQueryClient();

  const invalidateLists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }, [queryClient]);

  const invalidateMessages = useCallback(
    (conversationId: string) => {
      queryClient.invalidateQueries({ queryKey: ['conversation-messages', conversationId] });
    },
    [queryClient],
  );

  const createConversation = useCallback(
    async (input: {
      scope: ConversationScope;
      title?: string;
      metadata?: Record<string, unknown>;
    }): Promise<{ conversationId?: string; title?: string; error?: string }> => {
      const { data, error } = await callRpc<{
        success: boolean;
        conversation_id?: string;
        title?: string;
        error?: string;
      }>('create_conversation', {
        p_scope: input.scope,
        p_initial_title: input.title,
        p_metadata: input.metadata,
      });
      if (error) return { error: error.message };
      if (!data?.success) return { error: data?.error };
      invalidateLists();
      return { conversationId: data.conversation_id, title: data.title };
    },
    [invalidateLists],
  );

  const appendMessage = useCallback(
    async (input: {
      conversationId: string;
      role: 'user' | 'assistant' | 'system';
      content: string;
      intent?: Intent;
      intentOverride?: Intent;
      intentConfidence?: number;
      intentSource?: IntentSource;
      moderationEventId?: string;
      metadata?: Record<string, unknown>;
    }): Promise<{ messageId?: string; error?: string }> => {
      const { data, error } = await callRpc<{
        success: boolean;
        message_id?: string;
        error?: string;
      }>('append_message', {
        p_conversation_id: input.conversationId,
        p_role: input.role,
        p_content: input.content,
        p_intent: input.intent ?? null,
        p_intent_override: input.intentOverride ?? null,
        p_intent_confidence: input.intentConfidence ?? null,
        p_intent_source: input.intentSource ?? null,
        p_moderation_event_id: input.moderationEventId ?? null,
        p_metadata: input.metadata ?? null,
      });
      if (error) return { error: error.message };
      if (!data?.success) return { error: data?.error };
      invalidateMessages(input.conversationId);
      invalidateLists();
      return { messageId: data.message_id };
    },
    [invalidateMessages, invalidateLists],
  );

  const softDeleteConversation = useCallback(
    async (conversationId: string): Promise<{ success: boolean; error?: string }> => {
      const { data, error } = await callRpc<{ success: boolean; error?: string }>(
        'soft_delete_conversation',
        { p_conversation_id: conversationId },
      );
      if (error) return { success: false, error: error.message };
      if (!data?.success) return { success: false, error: data?.error };
      invalidateLists();
      return { success: true };
    },
    [invalidateLists],
  );

  return { createConversation, appendMessage, softDeleteConversation };
}
